import {
    ChangeEventHandler, FC, useMemo, useState,
} from 'react';
import { Icon } from '../../../../../../components/Icon';
import { TypesButton } from '../../../../../../constants/ButtonTypes';
import { colors } from '../../../../../../constants/Colors';
import { SizesButton } from '../../../../../../constants/SizeButton';
import {
    FeedPropertyDefinitions,
    FeedDefinitionPropertyCollection,
    TransformedFeedDefinitionPropertyState,
} from '../../../../../../types/Endpoints/feeds';
import { TypesIcon } from '../../../../../../types/TypesIcon';
import { TypesSelect } from '../../../../../../types/TypesSelect';
import { StyledCheckbox } from '../../styles';
import * as S from '../../styles';
import { Attribute } from './Attribute';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (properties: FeedDefinitionPropertyCollection) => void;
  definitions: FeedPropertyDefinitions;
  properties: FeedDefinitionPropertyCollection;
}

const EXTENDED_KEY = 'custom_label_0';
const CUSTOM_LABEL_PREFIX = 'custom_label_';
const CUSTOM_LABELS_KEY = 'custom_labels';

const extendDefinitions = (definitions: FeedPropertyDefinitions) => {
    const result = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < definitions.length; i++) {
        const definition = definitions[i];

        if (definition.key === EXTENDED_KEY) {
            result.push({
                key: CUSTOM_LABELS_KEY,
                name: 'Пользовательские метки',
                required: false,
                allowCustomValue: false,
                allowDuplicateProductType: false,
                predefinedValues: [],
                category: {
                    key: 'for_companies',
                    name: 'Для торговых кампаний',
                },
            });
        }

        result.push(definition);
    }

    return result;
};

const computeProperties = (properties: FeedDefinitionPropertyCollection, definitions: FeedPropertyDefinitions) => {
    const transformedDefinitions = definitions.reduce((acc, { required, key }) => {
        if (key === EXTENDED_KEY) {
            acc.custom_labels = {
                isEnabled: required,
                value: null,
                isDuplicateProductType: false,
            };
        }

        acc[key] = {
            isEnabled: required,
            value: null,
            isDuplicateProductType: false,
        };

        return acc;
    }, {} as TransformedFeedDefinitionPropertyState);

    if (!properties.length) {
        return transformedDefinitions;
    }

    properties.forEach(({
        key, value, isEnabled, isDuplicateProductType,
    }) => {
        if (key === EXTENDED_KEY) {
            transformedDefinitions.custom_labels = {
                isEnabled,
                value: null,
                isDuplicateProductType,
            };
        }

        transformedDefinitions[key] = {
            isEnabled,
            value,
            isDuplicateProductType,
        };
    });

    return transformedDefinitions;
};

export const AttributesDrawer: FC<Props> = ({
    isOpen, definitions, onClose, onSave, properties,
}) => {
    const extendedDefinitions = useMemo(() => extendDefinitions(definitions), [definitions]);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const definitionsGroups: Record<string, FeedPropertyDefinitions> = Object.groupBy(extendedDefinitions, ({ category }) => category.key);
    const categories: Record<string, string> = useMemo(() => Object.fromEntries(
        Array.from(
            new Set(extendedDefinitions.map(({ category }) => category.key)),
            key => {
                const category = definitions.find(d => d.category.key === key)?.category;
                return [category?.key, category?.name];
            },
        ),
    ), [extendedDefinitions]);

    const [props, setProps] = useState(computeProperties(properties, definitions));

    const handleSave = () => {
        const payload = Object.entries(props).map(([key, value]) => ({ ...value, key }));

        onSave(payload);
    };

    const onInputChange: ChangeEventHandler<HTMLInputElement> = e => {
        const { name, value } = e.currentTarget;

        setProps(prev => ({
            ...prev,
            [name]: {
                value: value || null,
                isEnabled: !!value,
                isDuplicateProductType: prev[name].isDuplicateProductType,
            },
        }));
    };

    const onInputCheck: ChangeEventHandler<HTMLInputElement> = e => {
        const { name, checked } = e.currentTarget;

        setProps(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                isEnabled: checked,
                ...(!checked && { value: null }),
            },
        }));
    };

    const onSelect = (field: string, name: string) => {
        setProps(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                value: name,
            },
        }));
    };

    return (
        <S.StyledDrawer anchor="right" open={isOpen}>
            <S.DrawerRoot>
                <S.DrawerHeading>
                    <S.ModalTitle>Настройка атрибутов</S.ModalTitle>
                    <S.CloseButton onClick={onClose}>
                        <Icon
                            type={TypesIcon.CLOSE_BIG}
                            color={colors.grey20}
                            width="24px"
                            height="24px"
                            viewBox="0 0 24 24"
                        />
                    </S.CloseButton>
                </S.DrawerHeading>
                {Object.entries(definitionsGroups).map(([key, value]) => (
                    <S.SectionContainer key={key}>
                        <S.Subtitle>{categories[key]}</S.Subtitle>
                        <S.CategoriesWrapper>
                            {value.map(item => {
                                if (item.key.includes(CUSTOM_LABEL_PREFIX)) {
                                    if (!props[CUSTOM_LABELS_KEY].isEnabled) {
                                        return null;
                                    }

                                    const setItemIsDuplicateProductType = (itemKey: string, isDuplicateProductType: boolean) => {
                                        setProps(prev => ({
                                            ...prev,
                                            [itemKey]: {
                                                ...prev[itemKey],
                                                isEnabled: !!prev[itemKey].value || isDuplicateProductType,
                                                isDuplicateProductType,
                                            },
                                        }));
                                    };

                                    return (
                                        <Attribute
                                            key={item.key}
                                            item={item}
                                            props={props}
                                            onInputChange={onInputChange}
                                            setItemIsDuplicateProductType={setItemIsDuplicateProductType}
                                        />
                                    );
                                }

                                return (
                                    <S.Row
                                        key={item.key}
                                    >
                                        <StyledCheckbox
                                            name={item.key}
                                            title={item.name}
                                            checked={props[item.key].isEnabled}
                                            disabled={item.required}
                                            onChange={onInputCheck}
                                        />
                                        {item.predefinedValues.length > 0 && (
                                            <S.StyledSelect
                                                name={item.key}
                                                options={item.predefinedValues.map(i => ({ ...i, id: i.value }))}
                                                selectType={TypesSelect.TRANSPARENT}
                                                value={props[item.key].value ?? ''}
                                                disabled={!props[item.key].isEnabled}
                                                label="Параметр"
                                                onClick={onSelect}
                                            />
                                        )}
                                    </S.Row>
                                );
                            })}
                        </S.CategoriesWrapper>
                    </S.SectionContainer>
                ))}
                <S.DrawerButtonsWrapper>
                    <S.DrawerButtonsContainer>
                        <S.StyledButton
                            typeButton={TypesButton.SECONDARY}
                            size={SizesButton.M}
                            onClick={onClose}
                        >
                            Отменить
                        </S.StyledButton>
                        <S.StyledButton
                            typeButton={TypesButton.PRIMARY}
                            size={SizesButton.M}
                            onClick={handleSave}
                        >
                            Сохранить изменения
                        </S.StyledButton>
                    </S.DrawerButtonsContainer>
                </S.DrawerButtonsWrapper>
            </S.DrawerRoot>
        </S.StyledDrawer>
    );
};
